import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Lottie from "react-lottie-player";
import ChatLottie from "../../../img/ChatBotLottie.json";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const obtenerFechaActual = () => {
  const dias = [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ];
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];

  const fecha = new Date();
  const diaSemana = dias[fecha.getDay()];
  const dia = fecha.getDate();
  const mes = meses[fecha.getMonth()];
  const año = fecha.getFullYear();

  return `${diaSemana} ${dia} de ${mes} del ${año}`;
};

const AsignarAgentesChatBot = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState({});
  const [loading, setLoading] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await fetch(
          "https://chatbot.injupemp.gob.hn/obtener_agentes/"
        );
        const data = await response.json();

        // Filtrar agentes no deseados
        // const agentesFiltrados = data.agentes.filter(agent =>
        //     !["Victor Sierra", "Kelin Ferrera", "Martha Triminio", "Erick Pineda", "Yohany Jimenez"].includes(agent.nombre)
        // );

        const agentesFiltrados = data.agentes.filter(
          (agent) => !agent.disponible === false
        );

        setAgents(agentesFiltrados);

        const initialSelection = {};
        agentesFiltrados.forEach((agent) => {
          initialSelection[agent.id] = agent.activo ? true : false;
        });
        setSelectedAgents(initialSelection);
      } catch (error) {
        console.error("Error fetching agents:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  const handleChange = (event) => {
    const { name, checked } = event.target;
    setSelectedAgents((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleAsignacion = async () => {
    const agentesSeleccionados = Object.keys(selectedAgents).filter(
      (id) => selectedAgents[id]
    );

    if (agentesSeleccionados.length === 0) {
      setAlertOpen(true);
    } else {
      setAlertOpen(false);
      try {
        await fetch(
          "https://chatbot.injupemp.gob.hn/actualizar_agentes_activos/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ agentes: agentesSeleccionados }),
          }
        );
        setDialogOpen(true);
      } catch (error) {
        console.error("Error updating agents:", error);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const fechaActual = obtenerFechaActual();

  // Separar encargados y agentes y ordenarlos alfabéticamente
  const encargados = agents
    .filter((agent) =>
      ["SCARLETH MENCIA", "ALEJANDRA VALERIANO", "ALEJANDRA LACAYO"].includes(
        agent.nombre.toUpperCase()
      )
    )
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const agentesRegulares = agents
    .filter(
      (agent) =>
        !["SCARLETH MENCIA", "ALEJANDRA VALERIANO", "ALEJANDRA LACAYO"].includes(
          agent.nombre.toUpperCase()
        )
    )
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  return (
    <Container style={{ marginTop: 15 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Asignar Agentes Para ChatBot INJUPEMP Virtual
        </Typography>
      </div>

      <Typography variant="h6" align="center" gutterBottom>
        {fechaActual}
      </Typography>

      <Box display="flex" justifyContent="center" alignItems="center">
        <Lottie
          loop
          animationData={ChatLottie}
          play
          style={{ width: "10%", height: "auto" }}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        {loading ? (
          <CircularProgress />
        ) : (
          <Card
            sx={{
              borderRadius: "12px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              maxWidth: 600,
              width: "100%",
              marginTop: 3,
              "& .MuiCardHeader-root": {
                backgroundColor: "primary.main",
                color: "white",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                py: 2,
                textAlign: "center",
              },
              "& .MuiCardHeader-title": {
                fontWeight: "bold",
                fontSize: "1.1rem",
              },
            }}
          >
            <CardHeader title="Seleccione los agentes a asignar en la siguiente lista:" />
            <CardContent>
              <FormGroup
                sx={{
                  "& .MuiFormControlLabel-root": {
                    mx: 0,
                    mb: 1,
                    borderRadius: "8px",
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  },
                  "& .MuiCheckbox-root": {
                    color: "grey",
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiTypography-root": {
                    fontWeight: 500,
                    color: "text.primary",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                }}
              >
                {/* Sección de Encargados */}
                <Typography
                  variant="h6"
                  sx={{
                    mt: 1,
                    mb: 2,
                    fontWeight: "bold",
                    color: "primary.main",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    pb: 1,
                  }}
                >
                  Encargados
                </Typography>

                {encargados.map((agent) => (
                  <FormControlLabel
                    key={agent.id}
                    control={
                      <Checkbox
                        checked={selectedAgents[agent.id] || false}
                        onChange={handleChange}
                        name={String(agent.id)}
                      />
                    }
                    label={
                      <>
                        <SupportAgentIcon
                          sx={{ color: "grey", fontSize: 30 }}
                        />
                        {agent.nombre.toUpperCase()}
                      </>
                    }
                  />
                ))}

                {/* Sección de Agentes */}
                <Typography
                  variant="h6"
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontWeight: "bold",
                    color: "primary.main",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    pb: 1,
                  }}
                >
                  Agentes
                </Typography>

                {agentesRegulares.map((agent) => (
                  <FormControlLabel
                    key={agent.id}
                    control={
                      <Checkbox
                        checked={selectedAgents[agent.id] || false}
                        onChange={handleChange}
                        name={String(agent.id)}
                      />
                    }
                    label={
                      <>
                        <SupportAgentIcon
                          sx={{ color: "grey", fontSize: 30 }}
                        />
                        {agent.nombre.toUpperCase()}
                      </>
                    }
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        )}
      </Box>

      {alertOpen && (
        <Alert severity="error" style={{ marginTop: "20px" }}>
          Se debe seleccionar al menos un agente para realizar la asignación.
        </Alert>
      )}

      <Grid container justifyContent="center" style={{ marginTop: "20px" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAsignacion}
          disabled={loading}
        >
          Realizar Asignación
        </Button>
      </Grid>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          <CheckCircleIcon style={{ color: "green", marginRight: "10px" }} />
          Asignación realizada con éxito
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Los agentes seleccionados han sido asignados correctamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AsignarAgentesChatBot;
